/**
 * RechtsAktenPage — Recht & Forderungen (8. Betrieb)
 * UX-Gerüst mit Status-Workflow: Forderung → Klage → Vergleich → Abgeschlossen
 * Farbe: Navy (#1E3A5F)
 */

import { useState, useCallback } from 'react';
import {
  Scale, Plus, Search, Filter, ChevronRight,
  FileText, Users, Mail, Clock, Gavel, Handshake,
  CheckCircle, AlertTriangle, Building2, User,
  ExternalLink, FolderOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import ForderungsmanagerDrawer from '@/components/ForderungsmanagerDrawer';
import QuickActionButtons from '@/components/QuickActionButtons';
import FristSetzenModal from '@/components/FristSetzenModal';

type RechtsStatus = 'forderung' | 'antwort' | 'klage' | 'vergleich' | 'abgeschlossen';

interface RechtsAkte {
  id: string;
  titel: string;
  mandant: string;
  gegner: string;
  anwalt: string;
  betrag: string;
  status: RechtsStatus;
  datum: string;
  aktenzeichen?: string;
  verknuepft?: string;
}

const STATUS_CONFIG: Record<RechtsStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  forderung: { label: 'Forderung eingereicht', color: '#2563EB', bg: 'rgba(37,99,235,0.08)', icon: FileText },
  antwort: { label: 'Antwort erhalten', color: '#D97706', bg: 'rgba(217,119,6,0.08)', icon: Mail },
  klage: { label: 'Klage', color: '#DC2626', bg: 'rgba(220,38,38,0.08)', icon: Gavel },
  vergleich: { label: 'Vergleich', color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', icon: Handshake },
  abgeschlossen: { label: 'Abgeschlossen', color: '#059669', bg: 'rgba(5,150,105,0.08)', icon: CheckCircle },
};

// Rechtsakten werden aus Supabase geladen (kein Demo-Daten)
const DEMO_AKTEN: RechtsAkte[] = [];

const STATUS_FLOW: RechtsStatus[] = ['forderung', 'antwort', 'klage', 'vergleich', 'abgeschlossen'];

function StatusBadge({ status }: { status: RechtsStatus }) {
  const cfg = STATUS_CONFIG[status];
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
      style={{ background: cfg.bg, color: cfg.color }}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

function FeatureBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-gray-300 font-medium">
      ○ {label}
    </span>
  );
}

export default function RechtsAktenPage() {
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<RechtsStatus | 'alle'>('alle');
  const [selected, setSelected] = useState<string | null>(null);
  const [forderungsDrawerOpen, setForderungsDrawerOpen] = useState(false);
  const [forderungsDrawerAkteId, setForderungsDrawerAkteId] = useState<string | undefined>(undefined);

  const handleOpenForderungsDrawer = useCallback((e: React.MouseEvent, akteId: string) => {
    e.stopPropagation();
    setForderungsDrawerAkteId(akteId);
    setForderungsDrawerOpen(true);
  }, []);

  const [fristModalOpen, setFristModalOpen] = useState(false);
  const [fristModalAkteId, setFristModalAkteId] = useState<string>('');
  const [fristModalAktenNummer, setFristModalAktenNummer] = useState<string | null>(null);

  const filtered = DEMO_AKTEN.filter(a => {
    const matchSearch = !search || a.titel.toLowerCase().includes(search.toLowerCase()) || a.mandant.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'alle' || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const handleOpenFristModal = useCallback((e: React.MouseEvent, akteId: string) => {
    e.stopPropagation();
    setFristModalAkteId(akteId);
    const akte = filtered.find(a => a.id === akteId);
    setFristModalAktenNummer(akte?.aktenzeichen ?? null);
    setFristModalOpen(true);
  }, [filtered]);

  const selectedAkte = DEMO_AKTEN.find(a => a.id === selected);

  // Kanban-Spalten
  const kanban = STATUS_FLOW.map(s => ({
    status: s,
    akten: DEMO_AKTEN.filter(a => a.status === s),
  }));

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Betrieb-Farbbalken */}
            <div className="w-1 h-8 rounded-full" style={{ background: 'linear-gradient(to bottom, #1E3A5F, #2563EB)' }} />
            <div>
              <div className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-[#1E3A5F]" />
                <h1 className="text-xl font-bold text-gray-900">Recht & Forderungen</h1>
                <span className="text-[10px] text-gray-300 font-medium border border-gray-200 rounded px-1.5 py-0.5">○ geplant</span>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">Rechtsakten · Forderungsmanagement · Anwalt-Kommunikation</p>
            </div>
          </div>
          <button
            onClick={() => toast.info('Neue Rechtsakte — ○ geplant')}
            className="flex items-center gap-2 bg-[#1E3A5F] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#1a3254] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Neue Rechtsakte
          </button>
        </div>

        {/* Status-Flow */}
        <div className="flex items-center gap-2 mt-4 overflow-x-auto pb-1">
          {STATUS_FLOW.map((s, i) => {
            const cfg = STATUS_CONFIG[s];
            const Icon = cfg.icon;
            const count = 0; // wird aus Supabase geladen
            return (
              <div key={s} className="flex items-center gap-2">
                <button
                  onClick={() => setFilterStatus(filterStatus === s ? 'alle' : s)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap border ${filterStatus === s ? 'border-current' : 'border-transparent bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                  style={filterStatus === s ? { background: cfg.bg, color: cfg.color, borderColor: cfg.color + '40' } : {}}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cfg.label}
                  {count > 0 && <span className="ml-1 bg-white/60 rounded-full px-1.5 text-[10px]">{count}</span>}
                </button>
                {i < STATUS_FLOW.length - 1 && <ChevronRight className="w-3 h-3 text-gray-300 flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 flex-1 max-w-sm">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Suche nach Mandant, Gegner, Aktenzeichen..."
            className="bg-transparent text-sm outline-none flex-1 text-gray-600 placeholder-gray-400"
          />
        </div>
        <button className="flex items-center gap-1.5 text-sm text-gray-500 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50" onClick={() => toast.info('Filter — ○ geplant')}>
          <Filter className="w-4 h-4" />
          Filter
        </button>
        <span className="text-xs text-gray-400">{filtered.length} Akten</span>
      </div>

      {/* Content: Liste + Detail */}
      <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
        {/* Akten-Liste */}
        <div className={`w-full md:w-96 border-b md:border-b-0 md:border-r border-gray-100 overflow-y-auto ${selectedAkte ? 'hidden md:block' : 'block'}`}>
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center px-6">
              <Scale className="w-8 h-8 text-gray-200 mb-2" />
              <p className="text-sm text-gray-400">Keine Rechtsakten gefunden</p>
            </div>
          ) : (
            filtered.map((akte, i) => (
              <button
                key={akte.id}
                onClick={() => setSelected(akte.id === selected ? null : akte.id)}
                className={`w-full text-left px-4 py-3.5 border-b border-gray-50 hover:bg-gray-50 transition-colors group ${selected === akte.id ? 'bg-blue-50/50' : i % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}
              >
                <div className="flex items-start gap-3">
                  {/* Farbiger linker Rand-Indikator */}
                  <div className="w-0.5 h-full rounded-full self-stretch" style={{ background: STATUS_CONFIG[akte.status].color, minHeight: '40px' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-sm font-semibold text-gray-800 truncate">{akte.id}</span>
                      <span className="text-xs font-bold text-[#1E3A5F] flex-shrink-0">{akte.betrag}</span>
                    </div>
                    <div className="text-xs text-gray-600 mb-1.5 truncate">{akte.titel}</div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={akte.status} />
                      {akte.verknuepft && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">{akte.verknuepft}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-400 mt-1.5">{akte.datum} · {akte.anwalt}</div>
                    <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <QuickActionButtons
                        akteId={akte.id}
                        // TODO: RechtsAkte has no forderungsakte_id/zahlungsstatus yet (DEMO data).
                        // Once real data is loaded from Supabase, pass the actual field values here
                        // so that button visibility reflects the correct context rules.
                        forderungsakte_id={null}
                        zahlungsstatus={null}
                        onNavigateToAkte={(e) => { e.stopPropagation(); setSelected(akte.id === selected ? null : akte.id); }}
                        onOpenForderungsDrawer={handleOpenForderungsDrawer}
                        onOpenFristModal={handleOpenFristModal}
                      />
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Detail-Panel */}
        <div className={`flex-1 overflow-y-auto ${!selectedAkte ? 'hidden md:block' : 'block'}`}>
          {selectedAkte ? (
            <div className="p-6 max-w-2xl">
              {/* Akte-Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg font-bold text-gray-900">{selectedAkte.id}</span>
                    <StatusBadge status={selectedAkte.status} />
                  </div>
                  <h2 className="text-base font-semibold text-gray-700">{selectedAkte.titel}</h2>
                  <div className="text-sm text-gray-500 mt-0.5">Erstellt: {selectedAkte.datum}</div>
                </div>
                <div className="text-xl font-bold text-[#1E3A5F]">{selectedAkte.betrag}</div>
              </div>

              {/* Beteiligte */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                {[
                  { label: 'Mandant', value: selectedAkte.mandant, icon: User, color: '#2563EB' },
                  { label: 'Gegner', value: selectedAkte.gegner, icon: Building2, color: '#DC2626' },
                  { label: 'Anwalt', value: selectedAkte.anwalt, icon: Scale, color: '#1E3A5F' },
                ].map((b, i) => (
                  <div key={i} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                    <div className="flex items-center gap-1.5 mb-1">
                      <b.icon className="w-3.5 h-3.5" style={{ color: b.color }} />
                      <span className="text-[10px] text-gray-400 font-medium uppercase tracking-wide">{b.label}</span>
                    </div>
                    <div className="text-sm font-medium text-gray-700">{b.value}</div>
                  </div>
                ))}
              </div>

              {/* Verknüpfte Akte */}
              {selectedAkte.verknuepft && (
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FolderOpen className="w-4 h-4 text-blue-500" />
                    <span className="text-sm text-blue-700">Verknüpft mit: <strong>{selectedAkte.aktenzeichen}</strong> ({selectedAkte.verknuepft})</span>
                  </div>
                  <button className="text-blue-500 hover:text-blue-700" onClick={() => toast.info('Verknüpfung zu Akte — ○ geplant')}>
                    <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              )}

              {/* Prozess-Schritte */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700">Prozess-Verlauf</h3>
                  <FeatureBadge label="Status-Tracking geplant" />
                </div>
                <div className="space-y-2">
                  {STATUS_FLOW.map((s, i) => {
                    const cfg = STATUS_CONFIG[s];
                    const Icon = cfg.icon;
                    const currentIdx = STATUS_FLOW.indexOf(selectedAkte.status);
                    const isDone = i < currentIdx;
                    const isCurrent = i === currentIdx;
                    return (
                      <div
                        key={s}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all ${isCurrent ? 'border-current' : isDone ? 'border-gray-100 bg-gray-50/50' : 'border-gray-100'}`}
                        style={isCurrent ? { background: cfg.bg, borderColor: cfg.color + '40' } : {}}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-green-100' : isCurrent ? '' : 'bg-gray-100'}`} style={isCurrent ? { background: cfg.bg } : {}}>
                          {isDone ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Icon className="w-4 h-4" style={{ color: isCurrent ? cfg.color : '#9CA3AF' }} />}
                        </div>
                        <span className={`text-sm font-medium ${isCurrent ? '' : isDone ? 'text-gray-400 line-through' : 'text-gray-400'}`} style={isCurrent ? { color: cfg.color } : {}}>
                          {cfg.label}
                        </span>
                        {isCurrent && <span className="ml-auto text-[10px] font-medium" style={{ color: cfg.color }}>Aktuell</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Funktions-Gerüst */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { label: 'Forderungsschreiben', desc: 'Vorlagen & Versand', icon: FileText, status: 'geplant' },
                  { label: 'Schriftverkehr', desc: 'E-Mail-Thread', icon: Mail, status: 'geplant' },
                  { label: 'Fristen-Kalender', desc: 'Automatische Erinnerungen', icon: Clock, status: 'geplant' },
                  { label: 'Anwalt-Zugang', desc: 'Eingeschränkte Sicht', icon: Users, status: 'geplant' },
                  { label: 'Dokumente', desc: 'Klageschrift, Urteile', icon: FolderOpen, status: 'geplant' },
                  { label: 'Abrechnung', desc: 'Honorar & Kosten', icon: AlertTriangle, status: 'geplant' },
                ].map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <button
                      key={i}
                      onClick={() => toast.info(`${f.label} — ○ geplant`)}
                      className="flex items-start gap-3 bg-gray-50 rounded-xl p-3 border border-gray-100 hover:bg-gray-100 transition-colors text-left"
                    >
                      <Icon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">{f.label}</div>
                        <div className="text-xs text-gray-400">{f.desc}</div>
                        <FeatureBadge label={f.status === 'geplant' ? 'geplant' : 'in Entwicklung'} />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center px-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'rgba(30,58,95,0.08)' }}>
                <Scale className="w-8 h-8 text-[#1E3A5F]" />
              </div>
              <h3 className="text-base font-semibold text-gray-700 mb-2">Recht & Forderungen</h3>
              <p className="text-sm text-gray-400 mb-4 max-w-xs">Rechtsakte auswählen um Details anzuzeigen. Forderungsmanagement, Anwalt-Kommunikation und Fristen-Tracking.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left w-full max-w-sm">
                {[
                  'Forderungsschreiben-Vorlagen',
                  'E-Mail-Thread mit Anwalt',
                  'Verknüpfung mit Akten',
                  'Anwalt-Zugang (Login)',
                  'Fristen-Kalender',
                  'Abschluss-Abrechnung',
                ].map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
                    <span className="text-[10px] text-gray-300">○</span>
                    <span className="text-xs text-gray-500">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Forderungsmanager Drawer */}
      <ForderungsmanagerDrawer
        open={forderungsDrawerOpen}
        onOpenChange={(open) => {
          setForderungsDrawerOpen(open);
          if (!open) setForderungsDrawerAkteId(undefined);
        }}
        akteId={forderungsDrawerAkteId}
      />

      {/* Frist setzen Modal */}
      <FristSetzenModal
        open={fristModalOpen}
        onClose={() => { setFristModalOpen(false); setFristModalAkteId(''); setFristModalAktenNummer(null); }}
        akteId={fristModalAkteId}
        aktenNummer={fristModalAktenNummer}
      />
    </div>
  );
}
